import { memo } from 'react'
import isString from 'lodash/isString'
import { Streamdown, type Components } from 'streamdown'

import { cn } from '@klear/ui/lib/utils'

const components: Components = {
  p: ({ children }) => <p className="text-sm leading-relaxed">{children}</p>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer noopener" className="text-primary underline underline-offset-2">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="text-muted-foreground line-through">{children}</del>,
  h1: ({ children }) => <h1 className="text-sm font-semibold">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold">{children}</h4>,
  h5: ({ children }) => <h5 className="text-sm font-semibold">{children}</h5>,
  h6: ({ children }) => <h6 className="text-sm font-semibold">{children}</h6>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5 text-sm">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5 text-sm">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed marker:text-muted-foreground">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 text-sm text-muted-foreground">{children}</blockquote>
  ),
  hr: () => <hr className="border-border" />,
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-md border border-border bg-muted p-3 font-mono text-xs">{children}</pre>
  ),
  code: ({ children }) => <code>{children}</code>,
  inlineCode: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.8125rem]">{children}</code>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
  tr: ({ children }) => <tr className="border-b border-border last:border-0">{children}</tr>,
  th: ({ children, style }) => (
    <th style={style} className="px-2 py-1 text-left font-medium text-muted-foreground">
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="px-2 py-1 align-top">
      {children}
    </td>
  ),
  img: ({ src, alt }) => <img src={isString(src) ? src : undefined} alt={alt} className="max-w-full rounded" />
}

export const AgentMarkdown = memo(function AgentMarkdown({
  text,
  isStreaming = false,
  className
}: {
  text: string
  isStreaming?: boolean
  className?: string
}) {
  return (
    <Streamdown
      className={cn('space-y-2 break-words', className)}
      components={components}
      controls={false}
      animated={false}
      mode={isStreaming ? 'streaming' : 'static'}
      parseIncompleteMarkdown={isStreaming}
    >
      {text}
    </Streamdown>
  )
})
